<template>
  <div class="AiDestination">
    <div v-if="picture?.srcSet" class="AiDestination-image">
      <ai-image
        :src="picture.src"
        :src-set="picture.srcSet"
        :alt="picture.alt ?? ''"
        :loading="lazyLoadImage ? 'lazy' : 'eager'" />
    </div>
    <div v-else class="AiDestination-image AiDestination-image--placeholder">
      <ai-icon name="photo" :size="32" class="AiDestination-image--icon" />
    </div>
    <div class="AiDestination-inner">
      <ai-typo
        class="AiDestination-title"
        as="h3"
        variant="heading-02"
        color="white">
        {{ title }}
      </ai-typo>
      <ai-typo
        class="AiDestination-description"
        as="p"
        variant="paragraph-04-regular"
        color="white">
        {{
          $t('ux.molecules.destination.description', {
            hotelNumber: hotelNumber,
          })
        }}
      </ai-typo>
      <ai-link
        v-if="link"
        class="AiDestination-link"
        :class="{ 'AiDestination-link--full': fullLink }"
        :url="link"
        :underline="false"
        :text="$t('ux.molecules.destination.linkLabel')"
        :aria-label="
          $t('ux.molecules.destination.linkLabel', { collection: title })
        "
        icon-right="button-arrow-right"
        color="gold-300"
        typo-variant="paragraph-04-regular" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { AiPicture } from '~~/domains/graphql';

type Props = {
  picture?: AiPicture | null;
  title?: string;
  link?: string;
  hotelNumber?: number | null;
  fullLink?: boolean;
  lazyLoadImage: boolean;
};

withDefaults(defineProps<Props>(), {
  picture: null,
  title: '',
  hotelNumber: 0,
  link: '',
});
</script>

<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/colors';
@use '@/assets/styles/utilities/mixins';
@use '@/assets/styles/utilities/mq';

.AiDestination {
  position: relative;
  display: flex;
  align-items: flex-end;
  width: 100%;
  padding: constants.$padding-04;
  text-decoration: none;
  overflow: hidden;

  background: linear-gradient(
    0deg,
    #000000 0%,
    rgba(0, 0, 0, 0.3) 49.11%,
    rgba(0, 0, 0, 0) 98.79%
  );

  @include mixins.rem-fallback(min-height, 300);

  @media (mq.$from-medium) {
    @include mixins.rem-fallback(min-height, 400);
  }

  &:hover {
    .AiDestination-image {
      transform: scale(1.1);
    }
  }
}

.AiDestination-image {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: -1;
  transition: transform 300ms;
}

.AiDestination-title {
  margin-bottom: constants.$inner-01;
}

.AiDestination-description {
  margin-bottom: constants.$margin-01;
}

.AiDestination-link {
  color: colors.$gold-300;
  text-decoration: none;
  text-transform: uppercase;

  &--full::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
  }
}
</style>
